export interface LogoutLifecycleHook {
  beforeLogoutRequest?: () => void | Promise<void>;
  afterLogoutRequest?: () => void | Promise<void>;
}

type LogoutLifecyclePhase = keyof LogoutLifecycleHook;

// Единственный подписчик — app/pwa (usePwaRuntime); слот вместо pub/sub-реестра.
let activeHook: LogoutLifecycleHook | null = null;

export const registerLogoutLifecycleHook = (hook: LogoutLifecycleHook): (() => void) => {
  activeHook = hook;

  return () => {
    if (activeHook === hook) {
      activeHook = null;
    }
  };
};

// Logout не должен зависать из-за cleanup-фазы: браузерные PWA-операции
// (например, serviceWorker.ready) могут никогда не резолвиться.
const LOGOUT_LIFECYCLE_DEADLINE_MS = 5_000;

const runPhase = async (phase: LogoutLifecyclePhase): Promise<void> => {
  const hook = activeHook;
  const handler = hook?.[phase];
  if (!hook || !handler) {
    return;
  }

  let deadlineHandle: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      handler.call(hook),
      new Promise<never>((_resolve, reject) => {
        deadlineHandle = setTimeout(() => {
          reject(new Error(`Logout lifecycle hook deadline exceeded: ${phase}`));
        }, LOGOUT_LIFECYCLE_DEADLINE_MS);
      }),
    ]);
  } catch {
    console.warn('[session.logout] Ошибка lifecycle hook', { phase });
  } finally {
    if (deadlineHandle !== undefined) {
      clearTimeout(deadlineHandle);
    }
  }
};

export const runBeforeLogoutRequest = (): Promise<void> => runPhase('beforeLogoutRequest');

export const runAfterLogoutRequest = (): Promise<void> => runPhase('afterLogoutRequest');
